import { useFieldArray, useWatch } from 'react-hook-form';
import { Alert, Button, Heading, Paragraph } from '@digdir/designsystemet-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useSolcelleFormContext } from '../utils';
import { KWP_SOFT_WARNING_TOTAL, useSolcellerValidation } from '../useSolcellerValidation';

import { InfoPopover } from './infoPopover';
import { PanelCard, PanelRowWrapper, PanelWrapper, RemoveButtonWrapper } from './styledComponents';
import { RemoveButton } from './removeButton';
import { SolarOrientationInput } from './solarOrientationGuide';
import { SolarPanelAmountInput } from './solarPanelAmountInput';
import { hasNorthFacingAzimuth } from './solarPanelWarnings';

export const KwpPanels = () => {
  const { t } = useTranslation();
  const { validateAzimuth, validateTilt, validateKwp, decimalPattern, integerPattern } = useSolcellerValidation();
  const { control, formState } = useSolcelleFormContext();
  const { fields, append, remove } = useFieldArray({ name: 'kwpPanels', control });
  const { errors } = formState;

  const kwpPanels = useWatch({ control, name: 'kwpPanels' });
  const totalKwp = useMemo(
    () =>
      (kwpPanels ?? []).reduce((sum, p) => {
        const e = p?.effect;
        return sum + (typeof e === 'number' && !Number.isNaN(e) ? e : 0);
      }, 0),
    [kwpPanels]
  );
  const showLargeSystemWarning = totalKwp > KWP_SOFT_WARNING_TOTAL;
  const showNorthFacingAzimuthWarning = hasNorthFacingAzimuth(kwpPanels);

  return (
    <PanelWrapper>
      <div>
        <Heading data-size="xs" level={3}>
          {t('register.solar.form.placement.label')}
        </Heading>
        <Paragraph>{t('register.solar.form.placement.info')}</Paragraph>
      </div>
      <InfoPopover />
      {fields.map((field, index) => (
        <PanelCard key={field.id}>
          <PanelRowWrapper aria-label={t('register.solar.form.orientationGuide.ariaLabel')} role="group">
            <SolarOrientationInput
              kind="azimuth"
              label={t('register.solar.form.panel.azimuth')}
              name={`kwpPanels.${index}.azimuth`}
              error={errors.kwpPanels?.[index]?.azimuth?.message}
              pattern={integerPattern}
              validate={validateAzimuth}
              value={kwpPanels?.[index]?.azimuth}
            />
            <SolarOrientationInput
              kind="tilt"
              label={t('register.solar.form.panel.tilt')}
              name={`kwpPanels.${index}.tilt`}
              error={errors.kwpPanels?.[index]?.tilt?.message}
              pattern={integerPattern}
              validate={validateTilt}
              value={kwpPanels?.[index]?.tilt}
            />
            <SolarPanelAmountInput
              suffix={t('register.solar.form.suffix.kwp')}
              label={t('register.solar.form.panel.effect')}
              name={`kwpPanels.${index}.effect`}
              error={errors.kwpPanels?.[index]?.effect?.message}
              pattern={decimalPattern}
              sliderMin={0}
              sliderMax={30}
              sliderStep={0.5}
              validate={validateKwp}
              value={kwpPanels?.[index]?.effect}
            />
          </PanelRowWrapper>
          {index > 0 && (
            <RemoveButtonWrapper>
              <RemoveButton onClick={() => remove(index)} />
            </RemoveButtonWrapper>
          )}
        </PanelCard>
      ))}
      <div>
        <Button variant={'secondary'} onClick={() => append({})}>
          {t('register.solar.form.buttons.add')}
        </Button>
      </div>
      {showLargeSystemWarning && (
        <Alert data-color="warning">
          <Paragraph>{t('register.solar.form.kwp.largeSystemWarning')}</Paragraph>
        </Alert>
      )}
      {showNorthFacingAzimuthWarning && (
        <Alert data-color="warning">
          <Paragraph>{t('register.solar.form.panel.azimuth.northFacingWarning')}</Paragraph>
        </Alert>
      )}
    </PanelWrapper>
  );
};
